import { CheckoutUpgradeSubscription } from '@/components/CheckoutSubscriptionPlan.tsx'
import { Loader } from '@/components/custom-components/Loader'
import { Button } from '@/components/shadcn/button'
import { Dialog, DialogContent, DialogPortal } from '@/components/shadcn/dialog'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@/components/shadcn/sidebar.tsx'
import { Tabs, TabsList, TabsTrigger } from '@/components/shadcn/tabs.tsx'
import useLogout from '@/hooks/useLogout'
import showToastNotification from '@/hooks/useShowToast'
import useUserPermissions from '@/hooks/useUserPermissions'
import { featureFlagKeys } from '@/utils/data/featureFlags'
import {
  DROP_SHIPPER,
  FREE_PLAN,
  PRO_PLAN,
  PlanDetail,
  SPARK_PLAN,
  STANDARD_PLAN,
  SUBSCRIPTION_STATUSES,
  VISION_PASS,
  plans,
} from '@/utils/data/plans.ts'
import { trpc } from '@/utils/trpc'
import { CreditCard, User, Users } from '@mynaui/icons-react'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import DowngradePlan from './DowngradePlan'
import UserProfileDialogPlansAndBillingSection from './UserProfileDialogPlansAndBillingSection'
import UserProfileDialogTeamsSection from './UserProfileDialogTeamsSection'
import { UserProfileEdit } from './UserProfileEdit'
import UserProfileSettingDetail from './UserProfileSettingDetail'
import { Text } from './custom-components'

export const UserProfileDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: () => void
}) => {
  const teamsFeatureFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.teams)

  const {
    permissions: permissionData,
    isLoading: fetchingPermissions,
    authUser: userData,
    isFetchingAuthUser: fetchingUserData,
    userIsOnSparkPlan: userOnSpark,
    userIsOnIgnitePlan: userOnStandard,
    userIsOnFreePlan: userOnFreePlan,
    isProUser: userOnPro,
  } = useUserPermissions()

  const sidebarOptions = {
    nav: teamsFeatureFlagEnabled
      ? [
          { name: 'My Account', icon: User },
          { name: 'Plan and Billing', icon: CreditCard },
          { name: 'Team', icon: Users },
        ]
      : [
          { name: 'My Account', icon: User },
          { name: 'Plan and Billing', icon: CreditCard },
        ],
  }

  const [darkModeValue, setDarkModeValue] = useLocalStorage('darkMode', false)
  const [edit, setEdit] = useState<boolean>(false)
  const [upgradeOpen, setUpgradeOpen] = useState<boolean>(false)
  const [cancelOpen, setCancelOpen] = useState<boolean>(false)
  const [selected, setSelected] = useState<PlanDetail | null>(null)
  const [subSection, setSubSection] = useState<string>(sidebarOptions.nav[0].name)

  const [subscriptionStatus, setSubscriptionStatus] = useState<SUBSCRIPTION_STATUSES>(undefined)

  const closeDialog = () => {
    setUpgradeOpen(false)
    setCancelOpen(false)
    setSelected(null)
    onOpenChange()
  }

  const handleCancel = () => {
    setCancelOpen(true)
  }

  const handleUpgrade = (plan: PlanDetail) => {
    setUpgradeOpen(true)
    setSelected(plan)
  }

  const { logout: logoutUser, loggingOut } = useLogout()

  const { mutate: resetUserPassword, isPending: sendingPasswordResetEmail } = trpc.sendResetPasswordEmail.useMutation()

  async function onResetPassword({ email }: { email: string }) {
    if (email) {
      try {
        resetUserPassword(
          { email },
          {
            onSuccess: () => {
              // let's keep the email in
              showToastNotification('success', {
                message: 'Request sent successfully!',
                description: 'We have sent you an email to complete the password reset',
              })
            },
            onError: (error) => {
              showToastNotification('error', {
                message: error.message,
              })
            },
          },
        )
      } catch (e) {
        console.log(e)
      }
    }
  }

  const { data: scheduledSubscriptionChanges } = trpc.getScheduledSubscriptionChanges.useQuery(undefined, {
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (open) {
      setSubSection(sidebarOptions.nav[0]?.name)
    }
  }, [open])

  useEffect(() => {
    if (permissionData && plans) {
      if (permissionData.userCanAccessEverything && permissionData.usersPlans.length === 0)
        setSubscriptionStatus('onFreeTrial')
      else if (
        permissionData.usersPlans.length > 0 &&
        permissionData.usersPlans.some((plan) =>
          [
            FREE_PLAN.plan_id,
            STANDARD_PLAN.plan_id,
            STANDARD_PLAN.no_trial_plan_id,
            PRO_PLAN.plan_id,
            PRO_PLAN.no_trial_plan_id,
            SPARK_PLAN.plan_id,
            SPARK_PLAN.no_trial_plan_id,
            DROP_SHIPPER.plan_id,
            VISION_PASS.plan_id,
          ].includes(plan),
        )
      ) {
        setSubscriptionStatus('hasPlan')
      } else {
        setSubscriptionStatus('hasNoPlan')
      }
    }
  }, [permissionData, teamsFeatureFlagEnabled])

  return (
    <Dialog open={open} onOpenChange={edit ? () => setEdit(false) : closeDialog}>
      <DialogPortal>
        {fetchingPermissions || fetchingUserData ? (
          <DialogContent className={''}>
            <div className="flex justify-center items-center w-full py-6 h-14">
              <Loader />
            </div>
          </DialogContent>
        ) : upgradeOpen && selected ? (
          <DialogContent className={'overflow-y-auto max-h-screen bg-themebackground dark:bg-themebackgrounddark'}>
            <CheckoutUpgradeSubscription userHasPlan={true} selectedPlan={selected} source="creativeos" />
          </DialogContent>
        ) : cancelOpen && permissionData ? (
          <DowngradePlan
            permissions={permissionData}
            subscriptionStatus={subscriptionStatus}
            currentPlan={
              userOnStandard
                ? 'standard'
                : userOnSpark
                  ? 'spark'
                  : userOnPro
                    ? 'pro'
                    : userOnFreePlan
                      ? 'free'
                      : undefined
            }
            scheduledSubscriptionPlan={scheduledSubscriptionChanges ? scheduledSubscriptionChanges.plan : undefined}
            closeModal={onOpenChange}
          />
        ) : (
          <DialogContent
            className={`overflow-hidden p-0 max-h-screen md:max-h-[700px] md:max-w-[700px] lg:max-w-[800px] bg-themebackground dark:bg-themebackgrounddark dark:darkthemefor`}
          >
            <div className="flex h-full">
              <SidebarProvider className="flex items-start max-w-46 ">
                <Sidebar
                  collapsible="none"
                  className="hidden md:flex w-full max-w-46 bg-themesidebar border-r-[1px] border-themesidebarborder dark:bg-themesidebardark dark:border-themesidebarborderdark"
                >
                  <SidebarContent>
                    <SidebarGroup className={'flex h-full max-h-[700px] flex-col justify-between w-full max-w-46 '}>
                      <SidebarGroupContent className="h-full w-full max-w-46 ">
                        <SidebarMenu>
                          {sidebarOptions.nav.map((item) => (
                            <SidebarMenuItem className={'cursor-pointer'} key={item.name}>
                              <SidebarMenuButton
                                asChild
                                className={`${item.name === subSection ? 'bg-themesidebarborder dark:bg-themesidebarborderdark' : ''} hover:bg-themesidebarborder dark:hover:bg-themesidebarborderdark`}
                                onClick={() => setSubSection(item.name)}
                              >
                                <span className={'flex gap-1.5 items-center '}>
                                  <item.icon className="min-w-5 w-5" />

                                  <Text weight={'medium'} size={'sm'}>
                                    {item.name}
                                  </Text>
                                </span>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          ))}
                        </SidebarMenu>
                      </SidebarGroupContent>
                      <SidebarGroupContent>
                        <SidebarMenu>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              className={'cursor-pointer'}
                              asChild
                              disabled={loggingOut}
                              onClick={() => {
                                logoutUser()
                                setDarkModeValue(darkModeValue)
                                document.body.classList.toggle('dark', false)
                              }}
                            >
                              <span className={''}>Log out</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  </SidebarContent>
                </Sidebar>
              </SidebarProvider>
              <div className="flex-1 h-full overflow-auto max-h-screen md:max-h-[700px] md:max-w-[700px] lg:max-w-[800px]">
                <Tabs
                  defaultValue={sidebarOptions.nav[0].name}
                  className="mt-5 w-full md:hidden"
                  value={subSection}
                  onValueChange={(val) => setSubSection(val)}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    {sidebarOptions.nav.map((item) => (
                      <TabsTrigger key={item.name} value={item.name} className="gap-2">
                        <item.icon className="h-5 w-5 min-w-5" /> {item.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                {subSection === 'Team' ? (
                  <UserProfileDialogTeamsSection />
                ) : subSection === 'Plan and Billing' ? (
                  <UserProfileDialogPlansAndBillingSection
                    subscriptionStatus={subscriptionStatus}
                    handleCancel={handleCancel}
                    handleUpgrade={handleUpgrade}
                  />
                ) : (
                  <div className="p-6">
                    {edit && userData ? (
                      <UserProfileEdit userData={userData} setEdit={setEdit} />
                    ) : (
                      <div className={`flex flex-col gap-6 ${!(edit || upgradeOpen) ? '' : 'hidden'}`}>
                        {userData && <UserProfileSettingDetail userData={userData} setEdit={setEdit} />}
                        <div className={'flex gap-2 mb-4'}>
                          <Button
                            variant={'outline'}
                            className="h-8"
                            onClick={() =>
                              onResetPassword({
                                email: userData?.email ?? '',
                              })
                            }
                            disabled={sendingPasswordResetEmail || !userData?.email}
                            loading={sendingPasswordResetEmail}
                          >
                            {sendingPasswordResetEmail ? 'Resetting Password...' : 'Reset Password'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        )}
      </DialogPortal>
    </Dialog>
  )
}
